import Alpine from 'alpinejs'

Alpine.data('craftsApp', () => ({
    sidebarOpen: false,
    toggleSidebar() { this.sidebarOpen = !this.sidebarOpen },
    closeSidebar() { this.sidebarOpen = false },

    // CSRF helper for fetch calls
    get csrfToken() {
        const el = document.querySelector('[name=csrfmiddlewaretoken]')
        return el ? el.value : ''
    },

    async postAction(url) {
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'X-CSRFToken': this.csrfToken },
        })
        return res
    },
}))

Alpine.data('photoGallery', () => {
    return {
        galleryOpen: false,
        imageURL: '',
        currentIndex: 0,
        images: [],
        touchStartX: 0,
        touchEndX: 0,
        init() {
            // Only get images that are direct children of the gallery container (not navigation arrows)
            this.images = Array.from(this.$el.querySelectorAll('.gallery-image'))
                .map(img => ({
                    url: img.getAttribute('src'),
                    height: img.getAttribute('height'), 
                    width: img.getAttribute('width')
                }));
        },
        photoClick(url) {
            this.galleryOpen = true;
            this.imageURL = url;
            this.currentIndex = this.images.findIndex(img => img.url === url);
        },
        nextPhoto() {
            if (this.currentIndex < this.images.length - 1) {
                this.currentIndex++;
                this.imageURL = this.images[this.currentIndex].url;
            }
        },
        prevPhoto() {
            if (this.currentIndex > 0) {
                this.currentIndex--;
                this.imageURL = this.images[this.currentIndex].url;
            }
        },
        isFirst() {
            return this.currentIndex === 0;
        },
        isLast() {
            return this.currentIndex === this.images.length - 1;
        },
        touchStart(event) {
            this.touchStartX = event.changedTouches[0].screenX;
        },
        touchEnd(event) {
            this.touchEndX = event.changedTouches[0].screenX;
            this.handleSwipe();
        },
        touchMove(event) {
            // Prevent default to stop scrolling when swiping in the gallery
            event.preventDefault();
        },
        handleSwipe() {
            const SWIPE_THRESHOLD = 50;
            if (this.touchStartX - this.touchEndX > SWIPE_THRESHOLD) {
                // Swipe left - go to next photo
                this.nextPhoto();
            } else if (this.touchEndX - this.touchStartX > SWIPE_THRESHOLD) {
                // Swipe right - go to previous photo
                this.prevPhoto();
            }
        }
    }
})

// Dashboard view
Alpine.data('dashboardView', () => {
    return {
        // Navigation
        sidebarOpen: false, // Sidebar drawer state (mobile)
        timeTab: 'summary', // 'summary' or 'entries' for timesheet page
        mobileMenuOpen: false, // Mobile menu state
        navOpen: false, // Navigation menu state (legacy)
        showStopAt: false, // Stop At datetime picker state
        stopDate: '', // Selected stop date
        stopTime: '', // Selected stop time
        showStartAt: false, // Start At datetime picker state
        startDate: '', // Selected start date
        startTime: '', // Selected start time
        // Aggregates
        days: [],
        periods: [],
        // Calendar state
        calendarYear: new Date().getFullYear(),
        calendarMonth: new Date().getMonth(), // 0-11
        get calendarLabel() {
            const d = new Date(this.calendarYear, this.calendarMonth, 1);
            return d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
        },
        get calendarMonthISO() {
            const y = this.calendarYear;
            const m = String(this.calendarMonth + 1).padStart(2, '0');
            return `${y}-${m}`;
        },
        get calendarCells() {
            // Build a 6x7 grid for the current month, Monday-first
            const firstOfMonth = new Date(this.calendarYear, this.calendarMonth, 1);
            const dow = (firstOfMonth.getDay() + 6) % 7; // 0=Mon
            const gridStart = new Date(firstOfMonth);
            gridStart.setDate(firstOfMonth.getDate() - dow);
            gridStart.setHours(0,0,0,0);
            const cells = [];
            // Index days map for quick lookup
            const dayMap = new Map(this.days.map(r => [r.key, r]));
            const today = new Date();
            today.setHours(0,0,0,0);
            for (let i = 0; i < 42; i++) {
                const d = new Date(gridStart);
                d.setDate(gridStart.getDate() + i);
                const inMonth = d.getMonth() === this.calendarMonth;
                const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                const row = dayMap.get(key);
                const hours = row ? row.hours : 0;
                const projects = row ? row.projects || [] : [];
                const isToday = d.getTime() === today.getTime();
                cells.push({
                    key,
                    dateIso: d.toISOString().slice(0,10),
                    day: d.getDate(),
                    inMonth,
                    isToday,
                    hours: Number(hours),
                    projects: projects,
                });
            }
            return cells;
        },
        prevMonth() {
            if (this.calendarMonth === 0) {
                this.calendarMonth = 11; this.calendarYear -= 1;
            } else { this.calendarMonth -= 1; }
            this.loadMonthData();
        },
        nextMonth() {
            if (this.calendarMonth === 11) {
                this.calendarMonth = 0; this.calendarYear += 1;
            } else { this.calendarMonth += 1; }
            this.loadMonthData();
        },
        todayMonth() {
            const n = new Date();
            this.calendarYear = n.getFullYear();
            this.calendarMonth = n.getMonth();
            this.loadMonthData();
        },
        async loadMonthData() {
            // For now, we'll use the existing data since it's already loaded
            // In the future, this could make an AJAX call to load data for the specific month
            // The current implementation loads all entries at page load, which is sufficient for most use cases
            this._computeAggregates(this.entries);
            this._computeTotals(this._filterEntries(this.entries));
        },
        // Period filter: 'all' | 'this_week' | 'this_month' | 'this_year' | 'custom'
        filterPeriod: 'this_month',
        customStart: '',
        customEnd: '',
        totals: { hours: 0, pre: 0, after: 0, taxes: 0 },
        billableTotals: { hours: 0, pre: 0, after: 0, taxes: 0 },
        // Stop-at input (datetime-local) in local time, e.g. 2025-01-31T14:05
        stopLocal: '',
        // Active timer state derived from DOM
        activeStart: null,
        activeRate: 0,
        activeTax: 0,
        activeNowMs: Date.now(),
        get activeHoursRaw() { return this.activeStart ? Math.max(0, (this.activeNowMs - this.activeStart.getTime()) / 1000 / 3600) : 0 },
        get activeHours() { return this.activeHoursRaw.toFixed(2) },
        get activePre() { return parseFloat(this.activeHoursRaw * this.activeRate).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}) },
        get activeAfter() { const pre = this.activeHoursRaw * this.activeRate; const after = pre - (pre * (this.activeTax / 100)); return parseFloat(after).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}) },
        get activeStartLocal() { return this.activeStart ? this.activeStart.toLocaleString() : '' },
        toLocal(isoString) {
            if (!isoString) return '';
            const d = new Date(isoString);
            return d.toLocaleString();
        },
        
        // Stop At widget functions
        setStopTime(minutesFromNow) {
            const now = new Date();
            const stopDateTime = new Date(now.getTime() + (minutesFromNow * 60 * 1000));

            // Format for date input (YYYY-MM-DD) using local date components
            this.stopDate = this._localDate(stopDateTime);

            // Format for time input (HH:MM)
            this.stopTime = this._localTime(stopDateTime);
        },
        
        resetStopTime() {
            this.stopDate = '';
            this.stopTime = '';
        },
        
        getStopDateTime() {
            if (!this.stopDate || !this.stopTime) return '';
            return `${this.stopDate}T${this.stopTime}`;
        },
        
        formatStopDateTime() {
            if (!this.stopDate || !this.stopTime) return '';
            const dt = new Date(`${this.stopDate}T${this.stopTime}`);
            return dt.toLocaleString();
        },
        
        getStopHours() {
            if (!this.stopDate || !this.stopTime || !this.activeStart) return 0;
            const stopDateTime = new Date(`${this.stopDate}T${this.stopTime}`);
            const hours = Math.max(0, (stopDateTime.getTime() - this.activeStart.getTime()) / 1000 / 3600);
            return hours.toFixed(2);
        },
        
        getStopPreTax() {
            const hours = parseFloat(this.getStopHours());
            const amount = hours * this.activeRate;
            return amount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
        },
        
        getStopAfterTax() {
            const hours = parseFloat(this.getStopHours());
            const pre = hours * this.activeRate;
            const after = pre - (pre * (this.activeTax / 100));
            return after.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
        },
        toIso(localDateTimeString) {
            if (!localDateTimeString) return '';
            const d = new Date(localDateTimeString);
            return d.toISOString();
        },
        
        // Start At widget functions
        setStartTime(minutesFromNow) {
            const now = new Date();
            const startDateTime = new Date(now.getTime() + (minutesFromNow * 60 * 1000));

            // Format for date input (YYYY-MM-DD) using local date components
            this.startDate = this._localDate(startDateTime);

            // Format for time input (HH:MM)
            this.startTime = this._localTime(startDateTime);
        },
        
        resetStartTime() {
            this.startDate = '';
            this.startTime = '';
        },
        
        getStartDateTime() {
            if (!this.startDate || !this.startTime) return '';
            return `${this.startDate}T${this.startTime}`;
        },
        
        formatStartDateTime() {
            if (!this.startDate || !this.startTime) return '';
            const dt = new Date(`${this.startDate}T${this.startTime}`);
            return dt.toLocaleString();
        },
        
        submitStartAt() {
            if (!this.startDate || !this.startTime) return;
            
            // Get the project and note from the main form
            const projectSelect = document.querySelector('select[name="project_id"]');
            const noteInput = document.querySelector('input[name="note"]');
            
            if (!projectSelect || !projectSelect.value) {
                alert('Please select a project first');
                return;
            }
            
            // Create a form and submit it
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = '/dashboard/entries/start-at/';
            
            // Add CSRF token
            const csrfToken = document.querySelector('[name=csrfmiddlewaretoken]').value;
            const csrfInput = document.createElement('input');
            csrfInput.type = 'hidden';
            csrfInput.name = 'csrfmiddlewaretoken';
            csrfInput.value = csrfToken;
            form.appendChild(csrfInput);
            
            // Add project ID
            const projectInput = document.createElement('input');
            projectInput.type = 'hidden';
            projectInput.name = 'project_id';
            projectInput.value = projectSelect.value;
            form.appendChild(projectInput);
            
            // Add note
            const noteHidden = document.createElement('input');
            noteHidden.type = 'hidden';
            noteHidden.name = 'note';
            noteHidden.value = noteInput ? noteInput.value : '';
            form.appendChild(noteHidden);
            
            // Add start time ISO
            const startTimeInput = document.createElement('input');
            startTimeInput.type = 'hidden';
            startTimeInput.name = 'start_time_iso';
            startTimeInput.value = this.toIso(this.getStartDateTime());
            form.appendChild(startTimeInput);
            
            // Submit the form
            document.body.appendChild(form);
            form.submit();
        },
        _localDate(d) {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        },
        _localTime(d) {
            const hh = String(d.getHours()).padStart(2, '0');
            const min = String(d.getMinutes()).padStart(2, '0');
            return `${hh}:${min}`;
        },
        _nowLocalInput() {
            const d = new Date();
            return `${this._localDate(d)}T${this._localTime(d)}`;
        },
        // Internal entries array (completed entries only)
        entries: [],
        _readEntriesFromDom() {
            const nodes = Array.from(this.$el.querySelectorAll('[data-entry]'));
            return nodes.map(n => ({
                start: n.getAttribute('data-start'),
                end: n.getAttribute('data-end'),
                rate: parseFloat(n.getAttribute('data-rate') || '0'),
                tax: parseFloat(n.getAttribute('data-tax') || '0'),
                project: n.getAttribute('data-project') || 'Unknown Project',
                billable: n.getAttribute('data-billable') !== 'false',
            }));
        },
        _periodStartEnd() {
            const now = new Date();
            if (this.filterPeriod === 'this_week') {
                const dow = (now.getDay() + 6) % 7; // 0=Mon
                const start = new Date(now);
                start.setDate(now.getDate() - dow);
                start.setHours(0,0,0,0);
                const end = new Date(start);
                end.setDate(start.getDate() + 6);
                end.setHours(23,59,59,999);
                return { start, end };
            }
            if (this.filterPeriod === 'this_month') {
                const start = new Date(now.getFullYear(), now.getMonth(), 1);
                const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                start.setHours(0,0,0,0);
                end.setHours(23,59,59,999);
                return { start, end };
            }
            if (this.filterPeriod === 'this_year') {
                const start = new Date(now.getFullYear(), 0, 1);
                const end = new Date(now.getFullYear(), 11, 31);
                start.setHours(0,0,0,0);
                end.setHours(23,59,59,999);
                return { start, end };
            }
            if (this.filterPeriod === 'custom') {
                if (this.customStart && this.customEnd) {
                    const start = new Date(this.customStart + 'T00:00:00');
                    const end = new Date(this.customEnd + 'T23:59:59.999');
                    return { start, end };
                }
                return { start: null, end: null };
            }
            return { start: null, end: null };
        },
        _filterEntries(entries) {
            const { start, end } = this._periodStartEnd();
            if (!start || !end) return entries;
            return entries.filter(e => {
                const s = new Date(e.start);
                return s >= start && s <= end;
            });
        },
        _computeTotals(entries) {
            let hours = 0, pre = 0, after = 0;
            let bHours = 0, bPre = 0, bAfter = 0;
            for (const e of entries) {
                const s = new Date(e.start);
                const t = new Date(e.end);
                const ms = Math.max(0, t - s);
                const h = ms / 1000 / 3600;
                const p = h * Number(e.rate);
                const a = p - (p * (Number(e.tax) / 100));
                hours += h; pre += p; after += a;
                if (e.billable) {
                    bHours += h; bPre += p; bAfter += a;
                }
            }
            const taxes = pre - after;
            this.totals = {
                hours: Number(hours.toFixed(2)),
                pre: Number(pre.toFixed(2)),
                after: Number(after.toFixed(2)),
                taxes: Number(taxes.toFixed(2)),
            };
            this.billableTotals = {
                hours: Number(bHours.toFixed(2)),
                pre: Number(bPre.toFixed(2)),
                after: Number(bAfter.toFixed(2)),
                taxes: Number((bPre - bAfter).toFixed(2)),
            };
        },
        _computeAggregates(entries) {
            const byDay = new Map();
            const byPeriod = new Map();
            for (const e of entries) {
                const start = new Date(e.start);
                const end = new Date(e.end);
                const ms = Math.max(0, end - start);
                const hours = ms / 1000 / 3600;
                const pre = hours * Number(e.rate);
                const after = pre - (pre * (Number(e.tax) / 100));
                const dayKey = start.getFullYear() + '-' + String(start.getMonth()+1).padStart(2,'0') + '-' + String(start.getDate()).padStart(2,'0');
                
                // Initialize day data with projects array
                if (!byDay.has(dayKey)) {
                    byDay.set(dayKey, { 
                        hours: 0, 
                        pre: 0, 
                        after: 0, 
                        date: new Date(start.getFullYear(), start.getMonth(), start.getDate()),
                        projects: new Map()
                    });
                }
                
                const dayData = byDay.get(dayKey);
                dayData.hours += hours;
                dayData.pre += pre;
                dayData.after += after;
                
                // Track project hours for this day
                const projectName = e.project || 'Unknown Project';
                if (!dayData.projects.has(projectName)) {
                    dayData.projects.set(projectName, 0);
                }
                dayData.projects.set(projectName, dayData.projects.get(projectName) + hours);
                console.log(`Added ${hours.toFixed(2)}h for ${projectName} on ${dayKey}`);
                
                const dow = (start.getDay() + 6) % 7; // 0 = Monday
                const periodStart = new Date(start);
                periodStart.setDate(start.getDate() - dow);
                periodStart.setHours(0,0,0,0);
                const periodEnd = new Date(periodStart);
                periodEnd.setDate(periodStart.getDate() + 6);
                periodEnd.setHours(23,59,59,999);
                const pKey = periodStart.toISOString().slice(0,10) + '_' + periodEnd.toISOString().slice(0,10);
                if (!byPeriod.has(pKey)) byPeriod.set(pKey, { hours: 0, pre: 0, after: 0, start: new Date(periodStart), end: new Date(periodEnd) });
                byPeriod.get(pKey).hours += hours;
                byPeriod.get(pKey).pre += pre;
                byPeriod.get(pKey).after += after;
            }
            this.days = Array.from(byDay.entries())
                .sort((a,b)=>a[1].date < b[1].date ? 1 : -1)
                .map(([k,v])=>({ 
                    key: k, 
                    label: v.date.toLocaleDateString(), 
                    hours: v.hours, 
                    pre: v.pre, 
                    after: v.after,
                    projects: Array.from(v.projects.entries()).map(([name, hours]) => ({ name, hours }))
                }));
            this.periods = Array.from(byPeriod.entries())
                .sort((a,b)=>a[1].start < b[1].start ? 1 : -1)
                .map(([k,v])=>({ key: k, label: v.start.toLocaleDateString() + ' – ' + v.end.toLocaleDateString(), hours: v.hours, pre: v.pre, after: v.after }));
        },
        init() {
            // Active entry
            const activeEl = this.$el.querySelector('[data-active-start]');
            if (activeEl) {
                const startIso = activeEl.getAttribute('data-active-start');
                const rateStr = activeEl.getAttribute('data-active-rate') || '0';
                const taxStr = activeEl.getAttribute('data-active-tax') || '0';
                if (startIso) this.activeStart = new Date(startIso);
                this.activeRate = parseFloat(rateStr);
                this.activeTax = parseFloat(taxStr);
                setInterval(() => { this.activeNowMs = Date.now() }, 1000);
            }
            // Default stop-at input value to now (local)
            this.stopLocal = this._nowLocalInput();
            // Read entries and compute
            this.entries = this._readEntriesFromDom();
            console.log('Dashboard entries loaded:', this.entries.length, this.entries);
            this._computeAggregates(this.entries);
            console.log('Days computed:', this.days.length, this.days);
            this._computeTotals(this._filterEntries(this.entries));
            // React to period change
            this.$watch('filterPeriod', () => {
                this._computeTotals(this._filterEntries(this.entries));
            });
            this.$watch('customStart', () => {
                if (this.filterPeriod === 'custom') this._computeTotals(this._filterEntries(this.entries));
            });
            this.$watch('customEnd', () => {
                if (this.filterPeriod === 'custom') this._computeTotals(this._filterEntries(this.entries));
            });
        }
    }
})



// Add Alpine object to the window scope
window.Alpine = Alpine

// initialize Alpine
Alpine.start()



import createGlobe from 'cobe';

const globeEl = document.getElementById('current-globe');
if (globeEl) {
  const canvas = globeEl.querySelector('canvas');
  let phi = 3.5; // Start rotated to show North America
  let width = globeEl.clientWidth;
  let height = globeEl.clientHeight;

  const globe = createGlobe(canvas, {
    devicePixelRatio: Math.min(window.devicePixelRatio, 2),
    width: width * 2,
    height: height * 2,
    phi: phi,
    theta: 0.15,
    dark: 1,
    diffuse: 1.2,
    scale: 1,
    mapSamples: 16000,
    mapBrightness: 6,
    baseColor: [0.1, 0.1, 0.1],
    markerColor: [0.976, 0.451, 0.086], // orange-500 (#f97316)
    glowColor: [0.2, 0.2, 0.2],
    markers: [
      { location: [44.9778, -93.2650], size: 0.1 }, // Minneapolis, MN
    ],
    onRender: (state) => {
      state.phi = phi;
      phi += 0.003;
      state.width = width * 2;
      state.height = height * 2;
    },
  });

  window.addEventListener('resize', () => {
    width = globeEl.clientWidth;
    height = globeEl.clientHeight;
  });
}